import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { useSlotContent } from '../../hooks/use-slot-content';
import { ButtonIcon } from './button-icon';
import { ButtonLabel } from './button-label';
import { ButtonLoading } from './button-loading';
import { ButtonRoot } from './button-root';
import type { ButtonPropsInterface } from './types/button.types';

/**
 * @since 1.0.5
 */
export const Button = forwardRef<HTMLButtonElement, ButtonPropsInterface>(
  (props: ButtonPropsInterface, forwardedRef) => {
    const { asChild = false, children, icon, iconPosition = 'start', ...rest } = props;

    const content = useSlotContent<'button'>({
      children,
      asChild,
      template: (childContent) => (
        <>
          {icon && iconPosition === 'start' ? <Button.Icon>{icon}</Button.Icon> : null}
          <Button.Label>{childContent}</Button.Label>
          {icon && iconPosition === 'end' ? <Button.Icon>{icon}</Button.Icon> : null}
        </>
      ),
      dependencies: [icon, iconPosition],
    });

    return (
      <Button.Root ref={forwardedRef} asChild={asChild} {...rest}>
        {content}
      </Button.Root>
    );
  },
) as ForwardRefExoticComponent<ButtonPropsInterface> & {
  Root: typeof ButtonRoot;
  Label: typeof ButtonLabel;
  Icon: typeof ButtonIcon;
  Loading: typeof ButtonLoading;
};

Button.displayName = 'Button';

Button.Root = ButtonRoot;
Button.Label = ButtonLabel;
Button.Icon = ButtonIcon;
Button.Loading = ButtonLoading;
